import { Alert, AlertColor, Box, Checkbox, CircularProgress, FormControl, FormControlLabel, FormGroup, Grid, InputLabel, MenuItem, Radio, RadioGroup, Select, TextField, Typography } from "@mui/material";
import { FC, useState } from "react";
import { useCreateViewMutation } from "../../generated";
import { useFormik } from "formik";
import * as yup from "yup";
import LoadingButton from "@mui/lab/LoadingButton";
import useListRegions from "../../hooks/useListRegions";

interface AlertMsg {
	severity: AlertColor;
	msg: string;
}

interface CreateViewProps {
    arn: string;
    region: string;
    setRegion: (index: string) => void;
	data: any;
	loading: boolean;
	setAlerts: (alerts: AlertMsg[]) => void
	changeTab: (
		index: number,
	) => void;
}

const validationSchema = yup.object().shape({
	createview: yup
		.string()
		.matches(/^[a-zA-Z0-9\-]{1,64}$/,"Please enter a valid name")
		.required("Please enter a valid name"),
	filterquery: yup
		.string(),
});

const CreateView: FC<CreateViewProps> = ({
	arn,
	region,
	setRegion,
	data,
	loading,
	setAlerts,
	changeTab
}: CreateViewProps) => {
	const regions = useListRegions();
	const [checked, setChecked] = useState(true);
	const [isAggregator, setIsAggregator] = useState(false);
	const [activeSpinner, setActiveSpinner] = useState(false); // spinner that displays while view is being created
	const [value, setValue] = useState('all-resources');

  const [createViewMutation] = useCreateViewMutation();

	//Handle Region Select
	const handleRegionSelect = (e: any) => {
		setRegion(e.target.value);
		const aggregator = data?.listIndexes?.items?.find((i: any) => i.Region === e.target.value);
		if (aggregator?.Type === "AGGREGATOR") {
			setIsAggregator(true);	
		} else {
			setIsAggregator(false);
		}
	};

	const formik = useFormik({
		initialValues: {
			createview: "",
			filterquery: "",
		},
		validationSchema,
		validateOnBlur: true,    // Runs validation when user leaves the field
		onSubmit: async (values) => {
			setActiveSpinner(true);
			const response = await createViewMutation({
				variables: {
					arn, // value for 'arn'
					ViewName: values.createview, // value for 'ViewName'
					FilterString: values.filterquery, // value for 'FilterString'
					tags: checked,// value for 'tags'
					region, // value for 'region'
				},
				context: {
					apiName: "resource_explorer"
				},
			});
			if (response?.data?.createView?.status === "COMPLETE"){
				setAlerts([
					{
						severity: "success",
						msg: values.createview + ` created successfully!`,
					},
				]);
				changeTab(1);
			} else if (response?.data?.createView?.status === "ERROR") {
				setAlerts([
					{
						severity: "error",
						msg: "Failed to create a view! Please check if a view with the specified name already exists in this AWS Region",
					},
				]);
			}
			setActiveSpinner(false);
		},
	});

	const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedValue = (event.target as HTMLInputElement).value;
		setValue(selectedValue);

		if (selectedValue === "all-resources") {
			formik.setFieldValue("filterquery", ""); // Reset filter query when switching
		}
	};

	const handleAdditionalAttributesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setChecked(event.target.checked);
	};

  return (
		<form onSubmit={formik.handleSubmit}>
		<Grid
			container
			sx={{ pb: 2, pt: 2, pl: 2 }}
			spacing={2}
			alignItems="stretch"
			flexDirection={"column"}
		>			
			<Grid item xs md={12}>
				<Typography
					component={"span"}
					variant="h6"
					color="text.primary"
					sx={{ mb: 1.5 }}
					paragraph
				>
					Create a Resource Explorer View in your AWS Account.
				</Typography>
			</Grid>
			<Grid item md={6}>
				<Box sx={{ minWidth: 120 }}>
					<FormControl fullWidth>
						<TextField
							fullWidth
							id="resource-explorer-create-view"
							placeholder="View Name"
							label="Create View Name"
							value={formik.values["createview"]}
							error={
								formik.touched["createview"] &&
								Boolean(formik.errors["createview"])
							}
							helperText={
								formik.touched["createview"] &&
								formik.errors["createview"]
									? String(formik.errors["createview"])
									: ""
							}
							onChange={(e) => formik.setFieldValue("createview", e.target.value)}
							onBlur={formik.handleBlur}
						/>
					</FormControl>
				</Box>
			</Grid>
			<Grid item md={6}></Grid>
			<Grid item md={6}>
				<Box sx={{ minWidth: 120 }}>
					<FormControl fullWidth>
						<InputLabel id="resource-explorer-view-region">
							Region
						</InputLabel>
						<Select
							required
							id="resource-explorer-view-region-select"
							name="region"
							value={data?.listIndexes?.items?.some((i: any) => i.Region === region) ? region : ""}
							label="Region"
							onChange={handleRegionSelect}
						>
							{data?.listIndexes?.items?.map((a: any) => {
								return (
									<MenuItem
										key={a.Arn}
										value={a.Region}
									>{`${regions.get(a.Region)} - ${a.Type}`}</MenuItem>
								);
							})}
						</Select>
					</FormControl>
				</Box>
			</Grid>
			<Grid item md={6}></Grid>
			{isAggregator ? (
				<Grid item md={12}>
					<Alert variant="outlined" severity="info">
						By default, this view can include resources from all AWS Regions in this account because the selected Region contains the aggregator index.
					</Alert>
				</Grid>
			) : null}	
			<Grid item xs md={12}>
				<Typography
					component={"span"}
					variant="subtitle2"
					color="text.primary"
					paragraph
				>
					Resources filter
				</Typography>
				<FormGroup>
					<RadioGroup
						aria-labelledby="demo-controlled-radio-buttons-group"
						name="controlled-radio-buttons-group"
						value={value}
						onChange={handleFilterChange}
					>
						<FormControlLabel disabled={true} value="all-resources" control={<Radio />} label="Include all resources" />
						{/* <FormControlLabel value="specific-resources" control={<Radio />} label="Include only resources that match a specified filter" /> */}
					</RadioGroup>
				</FormGroup>
			</Grid>
			{ value === "specific-resources" ? (
			<>
			<Grid item md={6}>
				<FormControl fullWidth>
					<TextField
						fullWidth
						id="create-view-filter"
						placeholder="Filter query"
						label="Filter query"
						value={formik.values["filterquery"]}
						error={
							formik.touched["filterquery"] &&
							Boolean(formik.errors["filterquery"])
						}
						helperText={
							formik.touched["filterquery"] &&
							formik.errors["filterquery"]
								? String(formik.errors["filterquery"])
								: ""
						}
						onChange={(e) => formik.setFieldValue("filterquery", e.target.value)}
						onBlur={formik.handleBlur}
					/>
				</FormControl>
			</Grid>
			<Grid item md={6}></Grid>
			</>
			): null}
			<Grid item xs md={12}>
				<Typography
					component={"span"}
					variant="subtitle2"
					color="text.primary"
					paragraph
				>
					Additional resource attributes
				</Typography>
				<Typography variant="body2">
					By default, the results include ARN, owner account, Region, service, and resource type.
				</Typography>
				<FormGroup>
					<FormControlLabel 
						control={
							<Checkbox
								checked={checked}
								onChange={handleAdditionalAttributesChange}
								inputProps={{ 'aria-label': 'controlled' }}
							/>
						} 
						label="Tags"
					/>
				</FormGroup>
			</Grid>
			<Grid item xs md={4} justifyContent="flex-end">
				<LoadingButton
					id="wat-setup-btn"
					variant="contained"
					color="secondary"
					type="submit"
					disabled={activeSpinner || data?.listIndexes === null}
					loading={activeSpinner || loading}
					sx={{
						width: 157,
						height: 40,
						bgcolor: "secondary.main",
						"&:hover": {
							bgcolor: "secondary.main",
							color: "secondary.contrastText",
						},
					}}
					loadingIndicator={
						<CircularProgress color={"secondary"} size={22} />
					}
				>
					Create View
				</LoadingButton>
			</Grid>
		</Grid>
		</form>
  )
};

export default CreateView;